import {
  Button,
  LoadingIndicator,
  Sheet,
  SheetContent,
  SheetTitle,
} from '@commenda/kakashi';
import { OnboardingStepNotificationEntity } from '@commenda/types/src/company/types';
import { AddEntityFormFields } from './AddEntityFormFields';
import { AddEntityFormData, useAddEntityForm } from './useAddEntityForm';

interface AddEntityBottomsheetProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  shouldNotResetForm?: boolean;
  onSuccess?: (corporation: OnboardingStepNotificationEntity) => Promise<void>;
  defaultValues?: Partial<AddEntityFormData>;
  disabledFields?: Partial<Record<keyof AddEntityFormData, boolean>>;
  allowEmptyEntityName?: boolean;
  mode?: 'admin' | 'user';
}

const AddEntityBottomsheet = ({
  isOpen,
  onClose,
  companyId,
  shouldNotResetForm,
  onSuccess,
  defaultValues,
  disabledFields,
  allowEmptyEntityName,
  mode,
}: AddEntityBottomsheetProps) => {
  const form = useAddEntityForm({
    onClose,
    companyId,
    shouldNotResetForm,
    onSuccess,
    defaultValues,
    allowEmptyEntityName,
    mode,
    includeNonControlledEntity: true,
    skipDateOfIncorporationValidation: true,
    skipFiscalYearEndValidation: true,
  });

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) form.requestClose();
      }}
    >
      <SheetContent side="bottom" preventCloseOnOutsideClick hideCloseIcon>
        {/* Scroll the body, not the panel, so the drag handle stays pinned/reachable. */}
        <div className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain flex flex-col items-center px-10 pt-5 pb-12">
          <form
            onSubmit={form.handleSubmit}
            className="w-full max-w-xl flex flex-col"
          >
            <div className="w-full flex flex-col gap-8">
              {/* SheetTitle wraps Radix DialogTitle (aria-labelledby
                target) — a plain h2 trips Radix's dev warning. */}
              <SheetTitle className="w-full text-2xl leading-8">
                Add entity
              </SheetTitle>

              {form.isLoading ? (
                <div className="w-full flex items-center justify-center py-8">
                  <LoadingIndicator size={64} />
                </div>
              ) : (
                <div className="w-full bg-bg-primary border border-border-secondary rounded-lg">
                  <AddEntityFormFields
                    form={form}
                    disabledFields={disabledFields}
                    showNonControlledEntity
                    hideDateOfIncorporation
                    hideFiscalYearEnd
                    hideEditCountryAlert
                    countryLabel="Select country"
                    companyLegalNamePlaceholder="Type here"
                    className="p-6 gap-5"
                  />
                </div>
              )}
            </div>

            <div className="w-full flex items-center justify-end gap-4 py-4">
              <Button
                variant="secondary"
                size="md"
                type="button"
                onClick={form.requestClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                type="submit"
                disabled={form.isLoading || form.isSubmitting || !form.isDirty}
              >
                {form.isSubmitting ? <LoadingIndicator size={24} /> : null}
                <span>Create entity</span>
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export { AddEntityBottomsheet };
